<script setup>
import { ref, computed } from 'vue'
import { useUserStore } from '@/stores/user'
import { Eye, CheckCircle, Code } from 'lucide-vue-next'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import TemplateFieldsRenderer from './TemplateFieldsRenderer.vue'

const userStore = useUserStore()
const isSuperAdmin = computed(() => userStore.member?.is_super_user)

const props = defineProps({
  fields: {
    type: Array,
    required: true,
  },
})

const open = ref(false)
const previewKey = ref(0)
const rendererRef = ref(null)
const validatedData = ref(null)
const showJson = ref(false)

const handleOpenChange = (val) => {
  open.value = val
  if (val) {
    previewKey.value++
    validatedData.value = null
    showJson.value = false
  }
}

const handleValidate = () => {
  if (rendererRef.value) {
    validatedData.value = rendererRef.value.getCollectedData()
  }
}

const formatValue = (row) => {
  if (row.value === null || row.value === undefined || row.value === '') return '-'
  if (row.type === 2) return row.value ? 'Yes' : 'No'
  return String(row.value)
}

const getFieldsJson = () => {
  return props.fields.map(f => ({
    key: f.key,
    type: f.type,
    ...(f.options?.length ? { options: f.options } : {}),
    ...(f.parent_temp_id ? { parent_temp_id: f.parent_temp_id } : {}),
    ...(f.parent_field_id ? { parent_field_id: f.parent_field_id } : {}),
    ...(f.trigger_operator ? { trigger_operator: f.trigger_operator } : {}),
    ...(f.trigger_value != null ? { trigger_value: f.trigger_value } : {}),
  }))
}
</script>

<template>
  <Dialog :open="open" @update:open="handleOpenChange">
    <DialogTrigger as-child>
      <Button type="button" variant="outline" size="sm">
        <Eye class="w-4 h-4 mr-1" />
        Preview
      </Button>
    </DialogTrigger>
    <DialogContent class="max-w-lg max-h-[80vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle>Template Preview</DialogTitle>
      </DialogHeader>

      <div v-if="isSuperAdmin" class="flex justify-end items-center gap-2">
        <Badge variant="secondary" class="text-xs">Admin</Badge>
        <Button type="button" variant="ghost" size="sm" @click="showJson = !showJson">
          <Code class="w-4 h-4 mr-1" />
          {{ showJson ? 'Form' : 'JSON' }}
        </Button>
      </div>

      <div v-if="showJson" class="border rounded-md overflow-hidden">
        <pre class="p-3 text-xs bg-muted overflow-x-auto">{{ JSON.stringify(getFieldsJson(), null, 2) }}</pre>
      </div>

      <template v-else>
        <TemplateFieldsRenderer ref="rendererRef" :key="previewKey" :fields="fields" />

        <div class="flex justify-end pt-2">
          <Button type="button" variant="secondary" size="sm" @click="handleValidate">
            <CheckCircle class="w-4 h-4 mr-1" />
            Validate
          </Button>
        </div>

        <div v-if="validatedData" class="border rounded-md overflow-hidden">
          <table class="w-full text-sm">
            <thead class="bg-muted">
              <tr>
                <th class="text-left px-3 py-2 font-medium">Field</th>
                <th class="text-left px-3 py-2 font-medium">Value</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, index) in validatedData" :key="index" class="border-t">
                <td class="px-3 py-2">{{ row.key }}</td>
                <td class="px-3 py-2">{{ formatValue(row) }}</td>
              </tr>
              <tr v-if="validatedData.length === 0" class="border-t">
                <td colspan="2" class="px-3 py-2 text-center text-muted-foreground">No visible fields</td>
              </tr>
            </tbody>
          </table>
        </div>
      </template>
    </DialogContent>
  </Dialog>
</template>
